import { DatePipe } from '@angular/common';
import { Component, computed, inject } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { MessageModule } from 'primeng/message';
import { NOTIFICATION_PORT } from '@app/ui/shared';
import { AiSessionCoordinator } from '../ai-session-coordinator';
import { AiSessionSettingsService } from '../ai-session-settings.service';
import { AiSessionRelayUrlDialogComponent } from './ai-session-relay-url-dialog.component';

@Component({
  selector: 'lib-ai-session-dialog',
  standalone: true,
  imports: [DatePipe, ButtonModule, MessageModule, TranslateModule],
  template: `
    <fieldset class="flex min-h-0 flex-1 flex-col pt-4 [@media(max-height:300px)]:pt-3">
      <section
        class="flex min-h-0 flex-1 flex-col rounded-2xl border border-white/10 bg-black/10 p-4 [@media(max-height:300px)]:p-0 [@media(max-height:300px)]:border-0"
      >
        <div class="flex items-center justify-between gap-3 min-h-[40px] [@media(max-height:300px)]:hidden">
          <h3 class="text-xs font-semibold uppercase tracking-[0.18em] text-muted-color">
            {{ 'screens.ai_session.titles.session' | translate }}
          </h3>
          <div class="flex min-w-0 items-center gap-2 opacity-50 text-muted-color">
            <span class="truncate text-xs max-w-[180px]">{{ relaySummary() || '—' }}</span>
            @if (!coordinator.active()) {
              <p-button
                type="button"
                severity="secondary"
                size="small"
                [text]="true"
                [rounded]="true"
                icon="pi pi-pencil"
                [ariaLabel]="'common.actions.edit' | translate"
                (onClick)="openRelayUrlEditor()"
              />
            }
          </div>
        </div>

        @if (!configured()) {
          <p-message severity="info" styleClass="mt-3 w-full">
            {{ 'screens.ai_session.notifications.configure_relay_first' | translate }}
          </p-message>
        } @else {
          <div
            class="mt-4 rounded-2xl border border-white/10 bg-black/5 px-4 py-5 text-center [@media(max-height:300px)]:mt-0"
          >
            <div class="flex items-center justify-center gap-2">
              <i [class]="statusClass()"></i>
              <span class="text-sm font-medium text-muted-color">
                {{ 'screens.ai_session.labels.status' | translate: { status: statusLabel() | translate } }}
              </span>
            </div>

            @if (coordinator.formattedRoomCode(); as roomCode) {
              @if (coordinator.status() === 'waiting') {
                <div class="mt-4 [@media(max-height:300px)]:mt-0">
                  <div class="flex items-center justify-center gap-2  [@media(max-height:300px)]:gap-0">
                    <div class="text-2xl font-semibold tracking-[0.35em]">
                      {{ roomCode }}
                    </div>
                    <p-button
                      type="button"
                      severity="secondary"
                      [text]="true"
                      [rounded]="true"
                      icon="pi pi-copy"
                      [ariaLabel]="'screens.ai_session.actions.copy_room_code' | translate"
                      (onClick)="copyRoomCode()"
                    />
                  </div>
                  <small class="mt-2 block text-muted-color [@media(max-height:300px)]:hidden">
                    {{ 'screens.ai_session.labels.room_code_hint' | translate }}
                  </small>
                  <small class="mt-1 block text-muted-color [@media(max-height:300px)]:hidden">
                    {{
                      'screens.ai_session.labels.expires'
                        | translate
                          : {
                              expiresAt: (coordinator.expiresAt() | date: 'medium' : undefined : dateLocale()),
                            }
                    }}
                  </small>
                </div>
              }
            }

            @if (coordinator.errorMessage(); as errorMessage) {
              <p-message severity="error" styleClass="mt-4 w-full">{{ errorMessage | translate }}</p-message>
            }
          </div>

          @if (coordinator.activityLog().length > 0) {
            <div
              class="mt-4 flex min-h-0 flex-1 flex-col rounded-2xl border border-white/10 bg-black/5 px-4 py-3 [@media(max-height:415px)]:hidden"
            >
              <div class="flex items-center justify-between gap-3">
                <h4 class="text-sm font-semibold text-muted-color">Recent commands</h4>
                <span class="text-xs text-muted-color">{{ coordinator.activityLog().length }}</span>
              </div>
              <div class="mt-3 min-h-0 flex-1 overflow-y-auto pr-1">
                <ul class="space-y-2">
                  @for (entry of coordinator.activityLog(); track entry.requestId) {
                    <li
                      class="flex items-start justify-between gap-3 rounded-xl border border-white/10 bg-surface-0/10 px-3 py-2"
                    >
                      <div class="min-w-0">
                        <div class="flex items-center gap-2">
                          <i
                            [class]="
                              entry.status === 'success'
                                ? 'pi pi-check-circle text-emerald-500'
                                : 'pi pi-times-circle text-red-500'
                            "
                          ></i>
                          <span class="truncate text-sm font-medium">{{ entry.name }}</span>
                        </div>
                        <div class="mt-1 text-xs text-muted-color">
                          {{ entry.finishedAt | date: 'shortTime' : undefined : dateLocale() }}
                          <span class="px-1">-</span>
                          {{ entry.durationMs }} ms
                        </div>
                      </div>
                      <span
                        class="shrink-0 rounded-full px-2 py-1 text-[10px] font-semibold uppercase tracking-wide"
                        [class.bg-emerald-100]="entry.status === 'success'"
                        [class.text-emerald-700]="entry.status === 'success'"
                        [class.bg-red-100]="entry.status !== 'success'"
                        [class.text-red-700]="entry.status !== 'success'"
                      >
                        {{ entry.status === 'success' ? 'ok' : 'failed' }}
                      </span>
                    </li>
                  }
                </ul>
              </div>
            </div>
          }
        }
      </section>
    </fieldset>

    <div class="p-dialog-form-footer [@media(max-height:300px)]:pt-0 [@media(max-height:300px)]:pb-3">
      <p-button
        type="button"
        severity="secondary"
        [outlined]="true"
        class="flex-1 [@media(max-height:300px)]:hidden"
        styleClass="w-full"
        [label]="'common.actions.close' | translate"
        (onClick)="onClose()"
      />
      @if (coordinator.active()) {
        <p-button
          type="button"
          severity="danger"
          [outlined]="true"
          class="flex-1"
          styleClass="w-full"
          [label]="'screens.ai_session.actions.stop' | translate"
          (onClick)="onStop()"
        />
      } @else {
        <p-button
          type="button"
          severity="primary"
          [outlined]="true"
          class="flex-1"
          styleClass="w-full"
          [label]="'screens.ai_session.actions.start' | translate"
          [disabled]="!configured()"
          (onClick)="onStart()"
        />
      }
    </div>
  `,
  styles: `
    :host {
      display: flex;
      flex-direction: column;
      flex: 1;
      min-height: 0;
      margin: -1rem;
    }
  `,
})
export class AiSessionDialogComponent {
  private readonly dialogRef = inject(DynamicDialogRef);
  private readonly translate = inject(TranslateService);
  private readonly notifications = inject(NOTIFICATION_PORT);
  private readonly dialogService = inject(DialogService);
  readonly settings = inject(AiSessionSettingsService);
  readonly coordinator = inject(AiSessionCoordinator);

  readonly configured = computed(() => this.settings.relayUrl().length > 0);
  readonly statusLabel = computed(() => `screens.ai_session.states.${this.coordinator.status().replaceAll('-', '_')}`);
  readonly statusIcon = computed(() => {
    switch (this.coordinator.status()) {
      case 'waiting':
        return 'pi pi-clock';
      case 'paired':
      case 'recently-active':
        return 'pi pi-check-circle';
      case 'starting':
        return 'pi pi-spin pi-spinner';
      case 'error':
        return 'pi pi-exclamation-triangle';
      default:
        return 'pi pi-circle';
    }
  });
  readonly statusTone = computed(() => {
    switch (this.coordinator.status()) {
      case 'waiting':
        return 'text-amber-500';
      case 'paired':
      case 'recently-active':
        return 'text-emerald-500';
      case 'starting':
        return 'text-sky-500';
      case 'error':
        return 'text-red-500';
      default:
        return 'text-muted-color';
    }
  });
  readonly statusClass = computed(() => `${this.statusIcon()} ${this.statusTone()}`);
  readonly relaySummary = computed(() => {
    const value = this.settings.relayUrl();
    if (!value) return '';
    try {
      const url = new URL(value);
      const pathname = url.pathname === '/' ? '' : url.pathname;
      return `${url.protocol}//${url.host}${pathname}`;
    } catch {
      return value;
    }
  });

  dateLocale(): string {
    const lang = this.translate.currentLang || this.translate.getDefaultLang() || 'en';
    if (lang.startsWith('uk')) return 'uk-UA';
    if (lang.startsWith('pl')) return 'pl-PL';
    if (lang.startsWith('ru')) return 'ru-RU';
    return 'en-US';
  }

  openRelayUrlEditor(): void {
    const ref = this.dialogService.open(AiSessionRelayUrlDialogComponent, {
      header: this.translate.instant('screens.ai_session.labels.relay_url'),
      styleClass: 'p-dialog-form w-11/12 max-w-2xl',
      modal: true,
      closable: true,
      data: { initialUrl: this.settings.relayUrl() },
    });

    ref?.onClose.subscribe((value) => {
      if (typeof value !== 'string' || !value.trim()) return;
      void this.coordinator.validateAndSaveRelayUrl(value).catch((error) => {
        this.notifications.error(
          error instanceof Error ? error.message : 'screens.ai_session.notifications.relay_invalid',
        );
      });
    });
  }

  async copyRoomCode(): Promise<void> {
    const code = this.coordinator.roomCode();
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      this.notifications.success(this.translate.instant('screens.ai_session.notifications.room_copied'));
    } catch (error) {
      console.warn('Failed to copy room code', error);
      this.notifications.error(this.translate.instant('screens.ai_session.notifications.room_copy_failed'));
    }
  }

  onStart(): void {
    void this.coordinator.start();
  }

  onStop(): void {
    void this.coordinator.stop();
  }

  onClose(): void {
    void this.coordinator.stop();
    this.dialogRef.close();
  }
}
